import type {
  ILitigatorCase,
  ILitigatorCaseDetails,
} from "src/interfaces/ComplianceGuide/ILitigatorSearch";
import CaseFacts from "./CaseFacts";
import CaseSummary from "./CaseSummary";
import styles from "./LitigatorSearch.module.css";

interface CaseDetailsProps {
  /** The search-result row. Everything here is available the moment the row opens. */
  caseData: ILitigatorCase;
  /**
   * Details fetched on expand, or null while loading / on failure. Carries the AI summary
   * and the statutory actions, neither of which the search response includes.
   */
  details: ILitigatorCaseDetails | null;
  isLoadingDetails: boolean;
  onDownloadPdf: (caseId: string) => void;
  isDownloading: boolean;
}

const CaseDetails = ({
  caseData,
  details,
  isLoadingDetails,
  onDownloadPdf,
  isDownloading,
}: CaseDetailsProps) => {
  const summary = details?.summary ?? null;
  // Prefer the detail response: search results leave `actions` empty.
  const actions = details?.actions ?? caseData.actions ?? null;

  // A case with no summary gives the facts rail the full width of the card, rather than
  // leaving a conspicuously empty column beside it.
  //
  // Which of the two layouts applies is settled before the details arrive, from a flag the
  // search hit already carries. Assuming a summary while the fetch was in flight opened
  // every case without one in the two-column layout and then collapsed it to the
  // full-width rail a moment later (DNC-358). Once the fetch has settled, the summary
  // itself is the authority — that also covers a fetch that failed, which leaves nothing
  // to put in the column however the flag reads.
  const showSummaryColumn = isLoadingDetails
    ? caseData.hasSummary
    : summary !== null;

  return (
    <div className={styles["case-details"]}>
      <div className={styles["case-card"]}>
        <div className={styles["case-card-header"]}>
          {/* Case number leads, title beneath. The row above already shows the title, but
              this panel runs tall enough to scroll it out of view, and the card should
              identify itself if it is read or captured on its own. */}
          <div className={styles["case-card-heading"]}>
            <h5 className={styles["case-card-number"]}>
              {caseData.caseNumber}
            </h5>
            {caseData.caseTitle && (
              <p className={styles["case-card-title"]}>{caseData.caseTitle}</p>
            )}
          </div>
          {caseData.fileName ? (
            <button
              type="button"
              className={styles["pdf-button"]}
              // stopPropagation for the same defensive reason as elsewhere in this panel:
              // it sits inside a grid row whose sibling cells toggle expansion, and Kendo
              // is free to add row-level handlers.
              onClick={(e) => {
                e.stopPropagation();
                onDownloadPdf(caseData.caseId);
              }}
              title="Download complaint PDF"
              disabled={isDownloading}
            >
              {isDownloading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    aria-hidden="true"
                  />
                  Downloading…
                </>
              ) : (
                <>
                  <i className="bi bi-download" aria-hidden="true" />
                  Complaint (PDF)
                </>
              )}
            </button>
          ) : (
            <span className={styles["pdf-unavailable"]}>PDF not available</span>
          )}
        </div>

        <div
          className={
            showSummaryColumn
              ? styles["case-card-body"]
              : `${styles["case-card-body"]} ${styles["case-card-body-facts-only"]}`
          }
        >
          {/* The summary arrives a moment after the row opens, so a placeholder holds the
              column and stops the facts rail jumping sideways once it lands. Only for a
              case that has one to wait for: the column is not there otherwise. */}
          {isLoadingDetails && showSummaryColumn && (
            <div className={styles["summary-placeholder"]} role="status">
              <span
                className="spinner-border spinner-border-sm me-2"
                aria-hidden="true"
              />
              Loading case summary…
            </div>
          )}
          {!isLoadingDetails && summary && <CaseSummary summary={summary} />}

          <CaseFacts
            caseData={caseData}
            summary={summary}
            actions={actions}
            isLoadingActions={isLoadingDetails}
            wide={!showSummaryColumn}
          />
        </div>
      </div>
    </div>
  );
};

export default CaseDetails;
